import { useEffect, useState, useRef } from 'react';

interface UseUnsavedChangesOptions {
  dependencies: unknown[];
  onCancel: () => void;
  confirmMessage?: string;
}

export const useUnsavedChanges = ({
  dependencies,
  onCancel,
  confirmMessage = 'You have unsaved changes. Are you sure you want to leave?',
}: UseUnsavedChangesOptions) => {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const initialValues = useRef<unknown[]>([]);
  const isInitialized = useRef(false);

  // Track form changes to detect unsaved changes
  useEffect(() => {
    // Store initial values when first loaded
    if (!isInitialized.current) {
      initialValues.current = [...dependencies];
      isInitialized.current = true;
      setHasUnsavedChanges(false);
      return;
    }

    // Compare current values with initial values
    const hasChanges = dependencies.some(
      (dep, index) => dep !== initialValues.current[index]
    );
    setHasUnsavedChanges(hasChanges);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  // Handle browser beforeunload for unsaved changes warning
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
      return undefined;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Block navigation (menu clicks, back button) when there are unsaved changes
  // Intercept all link clicks and ask for confirmation if there are unsaved changes
  useEffect(() => {
    if (!hasUnsavedChanges) return undefined;

    const handleClick = (e: MouseEvent) => {
      // Check if the click is on a link or inside a link
      const target = e.target as HTMLElement;
      const link = target.closest('a');

      if (link && link.href) {
        // Check if it's an internal navigation link (not external or hash links)
        const url = new URL(link.href);
        const currentUrl = new URL(window.location.href);

        if (
          url.origin === currentUrl.origin &&
          url.pathname !== currentUrl.pathname
        ) {
          // Internal navigation detected
          if (!window.confirm(confirmMessage)) {
            e.preventDefault();
            e.stopPropagation();
          }
        }
      }
    };

    // Capture phase to intercept before React Router processes the click
    document.addEventListener('click', handleClick, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [hasUnsavedChanges, confirmMessage]);

  // Handle cancel with confirmation
  const handleCancel = () => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm(confirmMessage);
      if (!confirmed) return;
    }
    onCancel();
  };

  // Reset initial values (useful after save)
  const resetInitialValues = () => {
    initialValues.current = [...dependencies];
    setHasUnsavedChanges(false);
  };

  return {
    hasUnsavedChanges,
    setHasUnsavedChanges,
    handleCancel,
    resetInitialValues,
  };
};
